import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom, Subscription } from 'rxjs';

import { TRANSLATION_PROVIDER } from './translation-provider';
import { TranslationCacheService } from './translation-cache.service';
import { flatten, unflatten } from './i18n-utils';

/**
 * Machine-vertaal-fallback: bij een taalwissel worden keys die wel in de basistaal (nl)
 * bestaan maar in de doeltaal ontbreken, via de TranslationProvider vertaald, in de actieve
 * vertaling samengevoegd en gecachet. De synchrone terugval (zolang dit nog loopt) verzorgt
 * Transloco zelf via `useFallbackTranslation`.
 */
@Injectable({ providedIn: 'root' })
export class TranslationFallbackService {
    private readonly __transloco = inject(TranslocoService);
    private readonly __provider  = inject(TRANSLATION_PROVIDER);
    private readonly __cache     = inject(TranslationCacheService);

    private readonly __baseLang = 'nl';
    private __subscr?: Subscription;

    init(): void {
        this.__subscr?.unsubscribe();
        this.__subscr = this.__transloco.langChanges$.subscribe(lang => this.__onLang(lang));
        this.__onLang(this.__transloco.getActiveLang());
    }

    private __onLang(lang: string): void {
        if (!lang || lang === this.__baseLang) return;
        // Zonder echte MT-provider niets doen: de Noop geeft de nl-bron terug, wat anders als
        // "vertaling" in de doeltaal zou worden gemerged/gecachet en de echte vertaling overschrijft.
        if (!this.__provider.enabled) return;

        // 1) Eerder gecachete vertalingen meteen toepassen.
        const _cached = this.__cache.get(lang);
        if (Object.keys(_cached).length > 0) {
            this.__transloco.setTranslation(unflatten(_cached), lang, { merge: true });
        }

        // 2) Ontbrekende keys (best-effort) bijvertalen.
        void this.__hydrate(lang);
    }

    private async __hydrate(lang: string): Promise<void> {
        const [_base, _target] = await Promise.all([
            firstValueFrom(this.__transloco.load(this.__baseLang)),
            firstValueFrom(this.__transloco.load(lang)),
        ]);

        const _baseFlat   = flatten(_base);
        const _targetFlat = flatten(_target);
        const _missing    = Object.keys(_baseFlat).filter(k => !_targetFlat[k]);
        if (_missing.length === 0) return;

        const _sources = _missing.map(k => _baseFlat[k]);

        let _translated: string[];
        try {
            _translated = await this.__provider.translate(_sources, this.__baseLang, lang);
        } catch {
            return; // Bij API-falen blijft de basistekst staan (Transloco-fallback).
        }

        const _partialFlat: Record<string, string> = {};
        _missing.forEach((k, i) => (_partialFlat[k] = _translated[i] ?? _sources[i]));

        this.__transloco.setTranslation(unflatten(_partialFlat), lang, { merge: true });
        this.__cache.merge(lang, _partialFlat);
    }
}
